import { useState } from "react";
import { useLocation, Navigate } from "@/lib/router-compat";
import qbdIvdQaradWhite from "@/assets/services/clinical-solutions/qbd-ivd-qarad-white.png";
import qbdIvdQaradLogo from "@/assets/services/clinical-solutions/qbd-ivd-qarad.png";
import SEOHead from "@/components/SEOHead";
import { getHubOgImage } from "@/lib/seo/hubOgImages";
import { heroTakeaway, getKeyTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import { questionHeading } from "@/data/questionHeadings";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import SplitBanner from "@/components/ui/SplitBanner";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getIndustryByPath } from "@/data/industriesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceValueProps from "@/components/services/ServiceValueProps";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import IndustryChallenges from "@/components/industries/IndustryChallenges";
import IndustryOfferings from "@/components/industries/IndustryOfferings";
import IndustrySteps from "@/components/industries/IndustrySteps";
import IndustryLifecycle from "@/components/industries/IndustryLifecycle";
import IndustrySolutions from "@/components/industries/IndustrySolutions";
import IndustryHowWeHelp from "@/components/industries/IndustryHowWeHelp";
import IndustryFAQ from "@/components/industries/IndustryFAQ";
import IndustrySponsorDelegation from "@/components/industries/IndustrySponsorDelegation";

import { useLanguage } from "@/hooks/useLanguage";
import { getIndustryI18n, ES_INDUSTRY_PATH_MAP, ZH_INDUSTRY_PATH_MAP } from "@/i18n/industryDetail";

const IndustryPage = () => {
  const location = useLocation();
  const lang = useLanguage();
  const [formOpen, setFormOpen] = useState(false);

  // Resolve localized paths to English data, then overlay i18n
  const enPath = ES_INDUSTRY_PATH_MAP[location.pathname] || ZH_INDUSTRY_PATH_MAP[location.pathname] || location.pathname;
  const industry = getIndustryByPath(enPath);
  if (!industry) return <Navigate to="/" replace />;

  const i18n = getIndustryI18n(industry.slug, lang);

  // Overlay i18n translations onto base industry data
  const d = i18n
    ? {
        ...industry,
        title: i18n.title,
        subtitle: i18n.subtitle,
        description: i18n.description,
        valueProps: i18n.valueProps,
        challenges: i18n.challenges,
        offerings: i18n.offerings,
        howWeHelp: i18n.howWeHelp ?? industry.howWeHelp,
        steps: i18n.steps ?? industry.steps,
        whyQbd: { ...industry.whyQbd, ...i18n.whyQbd },
        formHeading: i18n.formHeading,
        formSubtitle: i18n.formSubtitle,
        lifecycleCaption: i18n.lifecycleCaption ?? industry.lifecycleCaption,
        splitBanner: industry.splitBanner && i18n.splitBanner
          ? { ...industry.splitBanner, ...i18n.splitBanner }
          : industry.splitBanner,
        faqs: i18n.faqs ?? industry.faqs,
        banner: i18n.banner ? { ...industry.banner!, ...i18n.banner } : industry.banner,
      }
    : industry;

  // Build a ServiceData-compatible object for reused service components
  const serviceCompat = {
    ...d,
    logo: "",
    navLabel: d.slug.replace(/-/g, " "),
    challengesTitle: "",
    challengesContent: "",
    challengesImage: "",
    whyQbd: { ...d.whyQbd },
    contactDecoration: "",
    contactMailIcon: "",
    curatedContent: [],
  };

  const SITE = "https://qbdgroup.com";
  const DEFAULT_OG = "https://qbdgroup.com/og-image.png";
  const hubOg = getHubOgImage(location.pathname);
  const ogImage = hubOg ? `${SITE}${hubOg}` : d.heroImage?.startsWith("http") ? d.heroImage : `${SITE}${d.heroImage || ""}`;
  const finalOgImage = ogImage || DEFAULT_OG;

  // GEO: question-shaped H2s so each section answers a real query.
  const q = (section: Parameters<typeof questionHeading>[0]) => questionHeading(section, d.title, lang);

  const baseCrumb = lang === "es"
    ? { label: "Industrias", to: "/es/industrias" }
    : lang === "zh-cn"
    ? { label: "行业", to: "/zh-cn/industries" }
    : { label: "Industries", to: "/en/industries" };

  return (
    <>
      <SEOHead
        title={`${d.title} | QbD Group`}
        description={d.description.slice(0, 155)}
        canonical={`${SITE}${location.pathname}`}
        ogImage={finalOgImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: d.title,
            description: d.description,
            provider: { "@type": "Organization", name: "QbD Group", url: SITE },
            url: `${SITE}${location.pathname}`,
            ...keyTakeawaySchema(heroTakeaway(d.subtitle, d.description, getKeyTakeaway(d.slug, lang))),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: lang === "es" ? "Inicio" : "Home", item: `${SITE}/${lang === "es" ? "es" : ""}` },
              { "@type": "ListItem", position: 2, name: baseCrumb.label, item: `${SITE}${baseCrumb.to}` },
              ...(d.breadcrumbParent
                ? [{ "@type": "ListItem", position: 3, name: d.breadcrumbParent.label, item: `${SITE}${d.breadcrumbParent.to}` }]
                : []),
              { "@type": "ListItem", position: d.breadcrumbParent ? 4 : 3, name: d.title },
            ],
          })}
        </script>
        {/* FAQPage schema is emitted by <IndustryFAQ> from the FAQs actually rendered
            (database rows when present, hardcoded fallback otherwise). */}
      </SEOHead>
      

      <ScrollToTop />
      <Navbar logoOverride={d.slug === "in-vitro-diagnostics"
        ? { src: qbdIvdQaradWhite, srcDark: qbdIvdQaradLogo, alt: "QbD IVD | Qarad", className: "h-16 sm:h-[4.5rem] w-auto transition-opacity duration-300" }
        : undefined
      } />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': d.color } as React.CSSProperties}>
        <ServiceHero
          service={serviceCompat as any}
          onCtaClick={() => setFormOpen(true)}
          baseCrumb={baseCrumb}
          parentCrumbs={
            d.breadcrumbParent
              ? [{ label: d.breadcrumbParent.label, to: d.breadcrumbParent.to }]
              : undefined
          }
          partnerLogo={d.partnerLogo}
        />

        <ServiceValueProps valueProps={d.valueProps} color={d.color} />

        <IndustrySolutions industryPath={industry.path} additionalPaths={industry.additionalSolutionPaths} color={d.color} industryName={d.title} crossIndustrySolutions={industry.crossIndustrySolutions} extraCards={industry.extraSolutionCards} sectionTitle={q("solutions")} />

        {d.steps && d.steps.length > 0 && (
          <IndustrySteps steps={d.steps} color={d.color} sectionTitle={q("steps")} onCtaClick={() => setFormOpen(true)} />
        )}

        {d.splitBanner && (
          <SplitBanner
            title={d.splitBanner.title}
            description={d.splitBanner.description}
            image={d.splitBanner.image}
            imageAlt={d.splitBanner.imageAlt}
            ctaLabel={d.splitBanner.ctaLabel}
            onCtaClick={() => setFormOpen(true)}
            accentColor={d.color}
            reversed={d.splitBanner.reversed}
          />
        )}

        {d.lifecycleImage && (
          <IndustryLifecycle
            image={d.lifecycleImage}
            caption={d.lifecycleCaption}
            color={d.color}
          />
        )}

        {d.challenges.length > 0 && (
          <IndustryChallenges challenges={d.challenges} color={d.color} sectionTitle={q("challenges")} />
        )}

        {d.howWeHelp && d.howWeHelp.length > 0 && (
          <IndustryHowWeHelp cards={d.howWeHelp} color={d.color} sectionTitle={q("howWeHelp")} />
        )}

        {d.offerings.length > 0 && (
          <IndustryOfferings offerings={d.offerings} color={d.color} sectionTitle={q("offerings")} />
        )}

        <ServiceWhyQbd
          service={serviceCompat as any}
          onCtaClick={() => setFormOpen(true)}
          sectionLogo={d.slug === "in-vitro-diagnostics" ? qbdIvdQaradWhite : undefined}
          sectionTitle={q("whyQbd")}
        />

        {d.faqs && d.faqs.length > 0 && (
          <IndustryFAQ faqs={d.faqs} color={d.color} slug={d.slug} />
        )}

        <ServiceRelatedContent
          service={serviceCompat as any}
          contentTags={d.contentTags}
        />

        {/* HubSpot Form Dialog */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{d.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={d.title}
              hubspotFormId={d.hubspotFormId}
              heading={d.formHeading}
              subtitle={d.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={d.formHeading}
          description={d.formSubtitle}
        />
      </main>

      <Footer />
    </>
  );
};

export default IndustryPage;
